import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Building2, ChevronRight, Users, BookOpen, Calendar, ImageIcon } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorDashboard.css';

// Local storage key for persisting selected organization
const SELECTED_ORG_KEY = 'vibrantcast_curator_selected_org';

/**
 * Get the saved organization slug from localStorage
 */
export const getSavedOrganizationSlug = () => {
  try {
    return localStorage.getItem(SELECTED_ORG_KEY);
  } catch (e) {
    console.error('Error reading from localStorage:', e);
    return null;
  }
};

/**
 * Save the selected organization slug to localStorage
 */
export const saveOrganizationSlug = (slug) => {
  try {
    if (slug) {
      localStorage.setItem(SELECTED_ORG_KEY, slug);
    } else {
      localStorage.removeItem(SELECTED_ORG_KEY);
    }
  } catch (e) {
    console.error('Error writing to localStorage:', e);
  }
};

/**
 * Clear the saved organization selection
 */
export const clearSavedOrganization = () => {
  try {
    localStorage.removeItem(SELECTED_ORG_KEY);
  } catch (e) {
    console.error('Error clearing localStorage:', e);
  }
};

// Helper to add cloudinary transform
const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', `/upload/${transform}`);
};

/**
 * Landing page for curator dashboard where users can select an organization.
 * Shows all organizations the user has access to and persists their selection.
 */
const CuratorLandingPage = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [orgStats, setOrgStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkingRedirect, setCheckingRedirect] = useState(true);

  // Try to get context from parent layout
  let context;
  try {
    context = useOutletContext();
  } catch (error) {
    context = null;
  }

  // Check for saved organization and redirect if found
  useEffect(() => {
    const checkSavedOrg = async () => {
      const savedSlug = getSavedOrganizationSlug();
      
      if (savedSlug) {
        // Verify the user still has access to this organization
        try {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data: membership, error } = await supabase
              .from('organization_members')
              .select(`
                organization_id,
                organizations:organization_id (
                  organization_id,
                  slug,
                  name
                )
              `)
              .eq('user_uuid', user.id)
              .eq('is_active', true);

            if (!error && membership) {
              // Check if the saved org is in the user's memberships
              const hasAccess = membership.some(m => m.organizations?.slug === savedSlug);
              
              if (hasAccess) {
                // Redirect to the saved organization
                navigate(`/curator/organization/${savedSlug}`, { replace: true });
                return;
              } else {
                // User no longer has access, clear the saved preference
                clearSavedOrganization();
              }
            }
          }
        } catch (error) {
          console.error('Error checking saved organization access:', error);
        }
      }
      
      setCheckingRedirect(false);
    };

    checkSavedOrg();
  }, [navigate]);

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (checkingRedirect) return;
      
      try {
        // First check if we have organization memberships from context
        if (context?.organizationMemberships?.length > 0) {
          const orgs = context.organizationMemberships
            .map(m => m.organizations)
            .filter(Boolean);
          setOrganizations(orgs);
          
          // Fetch stats for each org
          await fetchOrgStats(orgs);
          setLoading(false);
          return;
        }

        // Otherwise, fetch the user's organization memberships
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setLoading(false);
          return;
        }

        const { data: memberships, error } = await supabase
          .from('organization_members')
          .select(`
            organization_id,
            role,
            organizations:organization_id (
              organization_id,
              slug,
              name,
              logo,
              description
            )
          `)
          .eq('user_uuid', user.id)
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching organization memberships:', error);
        } else if (memberships && memberships.length > 0) {
          const orgs = memberships.map(m => m.organizations).filter(Boolean);
          setOrganizations(orgs);
          
          // Fetch stats for each org
          await fetchOrgStats(orgs);
        }
      } catch (error) {
        console.error('Error in CuratorLandingPage:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [context, checkingRedirect]);

  // Fetch stats for organizations
  const fetchOrgStats = async (orgs) => {
    const stats = {};
    
    for (const org of orgs) {
      try {
        // Fetch collection count
        const { count: collectionCount } = await supabase
          .from('collections')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', org.organization_id);
        
        // Fetch event count
        const { count: eventCount } = await supabase
          .from('events')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', org.organization_id);
        
        // Fetch team member count
        const { count: memberCount } = await supabase
          .from('organization_members')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', org.organization_id)
          .eq('is_active', true);
        
        stats[org.organization_id] = {
          collections: collectionCount || 0,
          events: eventCount || 0,
          members: memberCount || 0
        };
      } catch (e) {
        console.error(`Error fetching stats for org ${org.organization_id}:`, e);
        stats[org.organization_id] = { collections: 0, events: 0, members: 0 };
      }
    }
    
    setOrgStats(stats);
  };

  const handleSelectOrganization = (org) => {
    // Save the selection to localStorage
    saveOrganizationSlug(org.slug);
    
    // Navigate to the organization's detail page
    navigate(`/curator/organization/${org.slug}`);
  };

  // Show loading while checking for redirect
  if (checkingRedirect || loading) {
    return (
      <div className="curator-overview">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{checkingRedirect ? 'Checking your preferences...' : 'Loading organizations...'}</p>
        </div>
      </div>
    );
  }

  // No organizations found
  if (organizations.length === 0) {
    return (
      <div className="curator-overview">
        <div className="empty-state">
          <Building2 size={48} className="empty-icon" style={{ color: '#9ca3af', marginBottom: '1rem' }} />
          <h2>Welcome to Curator Studio</h2>
          <p>You don't have access to any organizations yet.</p>
          <p>Contact an organization administrator to get added as a team member.</p>
        </div>
      </div>
    );
  }

  // Only one organization - redirect directly
  if (organizations.length === 1) {
    const org = organizations[0];
    saveOrganizationSlug(org.slug);
    navigate(`/curator/organization/${org.slug}`, { replace: true });
    return null;
  }

  // Show organization selection
  return (
    <div className="curator-overview">
      <div className="curator-landing-page">
        <div className="landing-header">
          <h1>Welcome to Curator Studio</h1>
          <p>Select an organization to get started</p>
        </div>

        <div className="org-selection-grid">
          {organizations.map(org => {
            const stats = orgStats[org.organization_id] || {};
            
            return (
              <button
                key={org.organization_id}
                className="org-selection-card"
                onClick={() => handleSelectOrganization(org)}
              >
                <div className="org-card-header">
                  <div className="org-card-logo">
                    {org.logo ? (
                      <img 
                        src={addCloudinaryTransform(org.logo, 't_fitmax-300/')}
                        alt={org.name}
                      />
                    ) : (
                      <div className="org-logo-placeholder">
                        <Building2 size={32} color="#059669" />
                      </div>
                    )}
                  </div>
                  <div className="org-card-info">
                    <h3>{org.name}</h3>
                    {org.description && (
                      <p className="org-card-description">{org.description}</p>
                    )}
                  </div>
                  <ChevronRight size={24} className="org-card-arrow" />
                </div>

                <div className="org-card-stats">
                  <div className="org-stat">
                    <BookOpen size={16} />
                    <span>{stats.collections || 0} Collections</span>
                  </div>
                  <div className="org-stat">
                    <Calendar size={16} />
                    <span>{stats.events || 0} Events</span>
                  </div>
                  <div className="org-stat">
                    <Users size={16} />
                    <span>{stats.members || 0} Members</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CuratorLandingPage;
